// Story 3.7d — community dashboard page.
import { useParams } from 'react-router';
import { Alert, Button, Skeleton, Space, Typography } from 'antd';
import { useOne } from '@refinedev/core';
import { useTranslation } from 'react-i18next';
import { BidiText } from '@lebinatikra/rtl-utils';
import { useDashboardMetrics } from './use-dashboard-metrics';
import { DashboardCards } from './dashboard-cards';

interface CommunityRow {
  id: string;
  name: string;
}

export function CommunityDashboard() {
  const { t } = useTranslation();
  const { communityId } = useParams<{ communityId: string }>();
  const id = communityId ?? '';

  const { data, isLoading, error, refetch } = useDashboardMetrics('community', id);
  const community = useOne<CommunityRow>({
    resource: 'communities',
    id,
    queryOptions: { enabled: Boolean(id) },
  });
  const name = community.query.data?.data?.name ?? '';

  return (
    <Space direction="vertical" size="large" style={{ width: '100%' }}>
      <Typography.Title level={3}>
        <BidiText>{t('dashboard.community.title', { name })}</BidiText>
      </Typography.Title>

      {isLoading && (
        <div data-testid="dashboard-skeleton">
          <Skeleton active />
        </div>
      )}

      {error && (
        <Alert
          type="error"
          message={(error as Error).message}
          data-testid="dashboard-error"
          action={
            <Button size="small" onClick={() => refetch()}>
              {t('common.retry')}
            </Button>
          }
        />
      )}

      {data && <DashboardCards metrics={data} />}
    </Space>
  );
}
